import { useEffect, useMemo, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useParams } from "react-router-dom";
import { useDispatch } from "react-redux";
import ModalComponent from "src/core/components/modal/modal.component";
import type { AsyncStatus } from "../components/types";
import { safeAtob } from "src/shared/helpers/enode-decode-string";
import { displayToast } from "src/shared/helpers/toaster.helper";
import { useCountdown } from "../components/hooks/use-countdown.hook";
import type {
  ActionMode,
  CommandResultRow,
  DaysActive,
  TimerProfile,
  TimerProfileFormValues,
} from "../components/types";
import {
  ACTION_MODE_OPTIONS,
  DAYS_ACTIVE_OPTIONS,
  DEFAULT_FORM_VALUES,
} from "./timer-profile.constants";
import { parseTimeToHHmm } from "./timer-profile.utils";
import "./timer-profile.scss";
import { TimerProfileToolbar } from "./components/timer-profile-toolbar.component";
import { TimerProfileTable, type TableDataRef } from "./components/timer-profile-table.component";
import { TimerProfileDetailCard } from "./components/timer-profile-detail-card.component";
import { TimerProfileFormModal } from "./components/timer-profile-form-modal.component";
import { TimerProfileHistory } from "./components/timer-profile-history.component";
import { TimerProfileCommandResultsModal } from "./components/timer-profile-command-results.component";
import { ProfileEnableDisableModal } from "./components/profile-enable-disable-modal.component";
import { AddZoneModal } from "./components/add-zone-modal.component";
import { useCommandTimer } from "../components/hooks/use-command-timer.hook";
import { useSocketAck } from "../components/hooks/use-socket-ack.hook";
import {
  getTimerProfileBaselineResult,
  getZoneInfo,
  sendGetCommandToDms,
  getTimerProfileCommandResult,
  updateTimerProfileStatus,
  getTimerProfileById,
  getDaysOfWeek,
  getOperationModes,
  setTimerProfile,
  updateTimerProfile,
  deleteTimerProfile,
  setLightProfileBaseline,
  applyBaselineToTimerProfile,
  getGatewayZone,
  type TimerProfileCommandResult,
  type TimerProfilePayload,
} from "src/core/api/services/command-module/timer-profile.service";
import { useTimerProfileValidation } from "./hooks/use-timer-profile-validation.hook";

const TimerProfileComponent = () => {
  const { id } = useParams<{ id: string }>();
  const assetCode = id ? safeAtob(id) : null;
  const dispatch = useDispatch();

  const [selectedProfile, setSelectedProfile] = useState<TimerProfile | null>(null);
  const [profileDetailData, setProfileDetailData] = useState<TimerProfile | null>(null);
  const [isLoadingProfileDetail, setIsLoadingProfileDetail] = useState(false);
  const [formModalMode, setFormModalMode] = useState<"add" | "edit" | null>(null);
  const [isHistoryModalOpen, setIsHistoryModalOpen] = useState(false);
  const [historyRefetchTrigger, setHistoryRefetchTrigger] = useState(0);
  const [isEnableDisableModalOpen, setIsEnableDisableModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoadingBackupRestore, setIsLoadingBackupRestore] = useState(false);
  const [isLoadingCommandResult, setIsLoadingCommandResult] = useState(false);
  const [enableDisableStatus, setEnableDisableStatus] = useState<AsyncStatus>("idle");
  const [isRestoreMode, setIsRestoreMode] = useState(false);
  const [isCommandResultModalOpen, setIsCommandResultModalOpen] = useState(false);
  const [enableDisableAction, setEnableDisableAction] = useState<"enable" | "disable" | null>(null);
  const [formValues, setFormValues] = useState<TimerProfileFormValues>(DEFAULT_FORM_VALUES);
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const timerValidation = useTimerProfileValidation(formValues);
  const [lastSyncDisplay, setLastSyncDisplay] = useState("Not available");
  const [commandResultData, setCommandResultData] = useState<TimerProfileCommandResult | null>(null);
  const [commandResultRawItems, setCommandResultRawItems] = useState<any[]>([]);
  const [baselineRows, setBaselineRows] = useState<CommandResultRow[] | null>(null);
  const [baselineRawItems, setBaselineRawItems] = useState<any[]>([]);
  const [restoreError, setRestoreError] = useState<string | null>(null);

  const buildTimerProfileBody = (items: any[]) =>
    items.map((item) => ({
      assetCode: assetCode ?? "",
      profileName: item.profileName ?? "",
      startTime: item.startTime,
      endTime: item.endTime,
      zoneID: Number(item.zoneId),
      daysOfWeeks: Number(item.daysOfWeeks),
      actionId: Number(item.actionId),
      repeatAction: Number(item.repeatAction ?? 0),
      brightLevel: Number(item.brightLevel),
      dimLevel: Number(item.dimLevel),
    }));
  const [isZoneInfoModalOpen, setIsZoneInfoModalOpen] = useState(false);
  const [zoneInfoStatus, setZoneInfoStatus] = useState<AsyncStatus>("idle");
  const [zoneInfoAssets, setZoneInfoAssets] = useState<{ id: number; name: string }[]>([]);
  const [zoneInfoError, setZoneInfoError] = useState<string | null>(null);
  const [tableDataRef, setTableDataRef] = useState<TableDataRef | null>(null);

  const enableDisablePendingRef = useRef(false);

  const { commandStatus, setCommandStatus, startCommandTimer, clearCommandTimer } = useCommandTimer();

  // Countdown for "Get Profile Config" — active while commandStatus is "pending"
  const { countdown: commandCountdown } = useCountdown({
    totalSeconds: 300,
    active: commandStatus === "pending",
    onExpire: () => setCommandStatus("failed"),
  });

  useSocketAck({
    ackKey: "TimerProfile",
    onAck: () => {
      clearCommandTimer();
      setCommandStatus("success");

      if (isHistoryModalOpen) {
        setHistoryRefetchTrigger((prev) => prev + 1);
      }

      if (enableDisablePendingRef.current) {
        enableDisablePendingRef.current = false;
        (async () => {
          try {
            if (tableDataRef) {
              await tableDataRef.refetch();
              const profiles = tableDataRef.getProfiles();
              const refreshed = profiles.find(
                (p) => p.id === selectedProfile?.id,
              );
              if (refreshed) {
                setSelectedProfile(refreshed);
                try {
                  const detailResponse = await getTimerProfileById(refreshed.id);
                  setProfileDetailData(detailResponse?.data || refreshed);
                } catch {
                  setProfileDetailData(refreshed);
                }
              }
            }
          } finally {
            setEnableDisableStatus("success");
          }
        })();
      }
    },
    assetCode: assetCode
  });

  useSocketAck({
    ackKey: "GetTimerProfileConfigFromDevice",
    onAck: () => {
      clearCommandTimer();
      setCommandStatus("success");
    },
    assetCode: assetCode
  });

  const isFormModalOpen = formModalMode !== null;

  const {
    data: daysOfWeekResponse,
  } = useQuery({
    queryKey: ["getDaysOfWeek"],
    queryFn: ({ signal }) => getDaysOfWeek(signal),
    enabled: isFormModalOpen,
  });

  const {
    data: operationModesResponse,
  } = useQuery({
    queryKey: ["getOperationModes"],
    queryFn: ({ signal }) => getOperationModes(signal),
    enabled: isFormModalOpen,
  });

  const {
    data: gatewayZonesResponse,
  } = useQuery({
    queryKey: ["getGatewayZone", assetCode],
    queryFn: () => getGatewayZone(assetCode || "", 0, 0),
    enabled: isFormModalOpen && !!assetCode,
  });

  const formDaysOptions = useMemo(
    () =>
      (daysOfWeekResponse?.data as { id: number; name: string }[] | undefined)?.map(
        (item) => item.name as DaysActive
      ) ?? DAYS_ACTIVE_OPTIONS,
    [daysOfWeekResponse]
  );

  const formActionModeOptions = useMemo(
    () =>
      (operationModesResponse?.data as { id: number; name: string }[] | undefined)?.map(
        (item) => item.name.replace("_", " ") as ActionMode
      ) ?? ACTION_MODE_OPTIONS,
    [operationModesResponse]
  );

  const formZoneOptions = useMemo(
    () =>
      (gatewayZonesResponse?.data as any[] | undefined)?.map((zone: any) => ({
        id: zone.zoneId,
        name: zone.zoneName,
      })) ??
      [],
    [gatewayZonesResponse]
  );

  useEffect(() => {
    if (enableDisableStatus === "success" || enableDisableStatus === "failed") {
      const timer = setTimeout(() => {
        setEnableDisableStatus("idle");
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [enableDisableStatus]);

  useEffect(() => {
    if (zoneInfoStatus === "success" || zoneInfoStatus === "failed") {
      const timer = setTimeout(() => {
        setZoneInfoStatus("idle");
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [zoneInfoStatus]);

  const handleSelectProfile = async (profile: TimerProfile) => {
    if (selectedProfile?.id === profile.id) return;
    setSelectedProfile(profile);
    setProfileDetailData(null);
    setIsLoadingProfileDetail(true);
    try {
      const response = await getTimerProfileById(profile.id);
      setProfileDetailData(response?.data || profile);
    } catch (error) {
      console.error("Failed to get timer profile details: ", error);
      setProfileDetailData(profile);
    } finally {
      setIsLoadingProfileDetail(false);
    }
  };

  const handleAddProfile = () => {
    setFormValues(DEFAULT_FORM_VALUES);
    setFormErrors({});
    timerValidation.reset();
    setFormModalMode("add");
  };

  const handleEditProfile = () => {
    if (!profileDetailData) return;
    setFormValues({
      name: profileDetailData.profileName,
      startTime: parseTimeToHHmm(profileDetailData.startTime),
      endTime: parseTimeToHHmm(profileDetailData.endTime),
      validAllDay: false,
      days: [profileDetailData.daysOfWeekName as any],
      dimLevel: profileDetailData.dimLevel,
      brightLevel: profileDetailData.brightLevel,
      actionMode: profileDetailData.actionName.replace("_", " ") as any,
      zoneId: profileDetailData.zoneId,
    });
    setFormErrors({});
    timerValidation.reset();
    setFormModalMode("edit");
  };

  const handleDeleteProfile = () => {
    if (!profileDetailData) return;
    setIsDeleteModalOpen(true);
  };

  const confirmDelete = async () => {
    if (!profileDetailData || !assetCode) return;

    try {
      await deleteTimerProfile(profileDetailData.id);
      // displayToast("Profile deleted successfully.", "green");
      setSelectedProfile(null);
      setProfileDetailData(null);
      if (tableDataRef) {
        await tableDataRef.refetch();
      }
    } catch (error: any) {
      console.error("Failed to delete timer profile: ", error);
      displayToast(
        error?.response?.data?.message || "Failed to delete profile.",
        "red"
      );
    } finally {
      setIsDeleteModalOpen(false);
    }
  };

  const handleViewHistory = () => {
    setIsHistoryModalOpen(true);
  };

  const handleCloseHistory = () => {
    setIsHistoryModalOpen(false);
  };

  const handleRestore = async () => {
    if (!assetCode) return;
    setIsRestoreMode(true);
    setRestoreError(null);
    setBaselineRows(null);
    setIsLoadingBackupRestore(true);

    try {
      const response = await getTimerProfileBaselineResult(assetCode, undefined);
      const rawItems = response?.data ?? [];
      const rows = rawItems.map((item: any) => ({
        zoneId: item.zoneId != null ? Number(item.zoneId) : null,
        schedule: `${item.startTime ?? ""} - ${item.endTime ?? ""}${item.dayOfWeek ? ` • ${item.dayOfWeek}` : ""
          }`,
        dimLevel: item.dimLevel != null ? Number(item.dimLevel) : null,
        brightLevel: item.brightLevel != null ? Number(item.brightLevel) : null,
        action: (item.action ?? "").replace(/_/g, " "),
        status: "--",
        dayOfWeek: item.dayOfWeek ?? null,
        actionId: item.actionId != null ? Number(item.actionId) : null,
      }));
      setBaselineRows(rows);
      setBaselineRawItems(rawItems);
      setLastSyncDisplay(new Date().toLocaleString());
    } catch (error: any) {
      console.error("Failed to get timer profile baseline result: ", error);
      setRestoreError(
        error?.response?.data?.message || "Failed to load baseline profile data."
      );
      setBaselineRows([]);
    } finally {
      setIsLoadingBackupRestore(false);
      setIsCommandResultModalOpen(true);
    }
  };

  const handleGetZoneInfo = async () => {
    if (!assetCode || !selectedProfile?.zoneId) return;
    setZoneInfoStatus("pending");
    setZoneInfoError(null);
    setZoneInfoAssets([]);

    try {
      const response = await getZoneInfo(assetCode, selectedProfile.zoneId, undefined);
      const assets = response?.data?.assets || response?.data || [];
      setZoneInfoAssets(assets);
      setZoneInfoStatus("success");
      setIsZoneInfoModalOpen(true);
    } catch (error: any) {
      console.error("Failed to get zone info: ", error);
      const message = error?.response?.data?.message || "Failed to load zone info.";
      setZoneInfoError(message);
      setZoneInfoStatus("failed");
    }
  };

  const handleCloseZoneInfoModal = () => {
    setIsZoneInfoModalOpen(false);
  };

  const handleSaveZone = (zoneName: string, assetId: number | null) => {
    if (!selectedProfile) return;

    setSelectedProfile({
      ...selectedProfile,
      zoneId: assetId ?? selectedProfile.zoneId,
      group: zoneName || selectedProfile.group,
    });

    setIsZoneInfoModalOpen(false);
    setZoneInfoStatus("idle");
    setZoneInfoAssets([]);
    setZoneInfoError(null);
    // displayToast("Zone saved successfully.", "green");
  };

  const handleGetProfileConfig = async () => {
    if (!assetCode) return;

    setCommandStatus("pending");
    startCommandTimer();
    setCommandResultData(null);
    setIsCommandResultModalOpen(false);

    try {
      const response = await sendGetCommandToDms(assetCode, 1);
      displayToast(response?.message, "green");
    } catch (error: any) {
      console.error("Failed to send get profile config command: ", error);
      clearCommandTimer();
      setCommandStatus("failed");
      displayToast(
        error?.response?.data?.message || error?.message || "Failed to send get profile config command.",
        "red"
      );
    }
  };

  const handleViewCommandResult = async () => {
    if (!assetCode) return;
    setIsLoadingCommandResult(true);

    try {
      const response = await getTimerProfileCommandResult(assetCode, undefined);
      const rawItems = response?.data ?? [];
      const rows = rawItems.map((item: any) => ({
        zoneId: item.zoneId != null ? Number(item.zoneId) : null,
        schedule: `${item.startTime ?? ""} - ${item.endTime ?? ""}${item.dayOfWeek ? ` • ${item.dayOfWeek}` : ""
          }`,
        dimLevel: item.dimLevel != null ? Number(item.dimLevel) : null,
        brightLevel: item.brightLevel != null ? Number(item.brightLevel) : null,
        action: (item.action ?? "").replace(/_/g, " "),
        dayOfWeek: item.dayOfWeek ?? null,
        actionId: item.actionId != null ? Number(item.actionId) : null,
        status: "--",
      }));
      setCommandResultData({ lastSync: new Date().toISOString(), profiles: rows });
      setCommandResultRawItems(rawItems);
      setLastSyncDisplay(new Date().toLocaleString());
      setIsCommandResultModalOpen(true);
    } catch (error: any) {
      console.error("Failed to get timer profile config from device: ", error);
      setCommandResultData({
        lastSync: new Date().toISOString(),
        profiles: [],
      });
      setCommandResultRawItems([]);
      setLastSyncDisplay(new Date().toLocaleString());
      setIsCommandResultModalOpen(true);
    } finally {
      setIsLoadingCommandResult(false);
    }
  };

  const handleCloseCommandResult = () => {
    setIsCommandResultModalOpen(false);
    setIsRestoreMode(false);
    setRestoreError(null);
    setBaselineRows(null);
  };

  const handleBackup = async () => {
    if (!assetCode || commandResultRawItems.length === 0) return;
    setIsLoadingBackupRestore(true);

    try {
      const body = buildTimerProfileBody(commandResultRawItems);
      await setLightProfileBaseline(body);
      setIsCommandResultModalOpen(false);
      setIsRestoreMode(false);
      setCommandResultRawItems([]);
    } catch (error: any) {
      console.error("Failed to backup timer profile: ", error);
      displayToast(
        error?.response?.data?.message || "Failed to backup configuration.",
        "red"
      );
    } finally {
      setIsLoadingBackupRestore(false);
    }
  };

  const handleRestoreConfig = async () => {
    if (!assetCode || baselineRawItems.length === 0) return;
    setIsLoadingBackupRestore(true);
    setRestoreError(null);

    try {
      const body = buildTimerProfileBody(baselineRawItems);
      const response = await applyBaselineToTimerProfile(body);
      if (response?.status === 200) {
        setIsCommandResultModalOpen(false);
        setIsRestoreMode(false);
        setBaselineRows(null);
        setBaselineRawItems([]);
        if (tableDataRef) {
          await tableDataRef.refetch();
          const refreshed = tableDataRef.getProfiles().find(
            (p) => p.id === selectedProfile?.id,
          );
          if (refreshed) {
            setSelectedProfile(refreshed);
            try {
              const detailResponse = await getTimerProfileById(refreshed.id);
              setProfileDetailData(detailResponse?.data || refreshed);
            } catch {
              setProfileDetailData(refreshed);
            }
          }
        }
      }
    } catch (error: any) {
      console.error("Failed to restore timer profile: ", error);
      setRestoreError(
        error?.response?.data?.message || "Failed to restore profile."
      );
    } finally {
      setIsLoadingBackupRestore(false);
    }
  };

  const handleSaveProfile = async (values: TimerProfileFormValues) => {
    if (!assetCode) return;

    const isValid = timerValidation.validate();
    if (!isValid) {
      return;
    }

    setIsSubmitting(true);
    setFormErrors({});

    const daysOfWeekData = daysOfWeekResponse?.data as
      | { id: number; name: string }[]
      | undefined;
    const operationModeData = operationModesResponse?.data as
      | { id: number; name: string }[]
      | undefined;

    const selectedDay = daysOfWeekData?.find(
      (item) => item.name === values.days[0]
    );
    const selectedAction = operationModeData?.find(
      (item) => item.name.replace("_", " ") === values.actionMode
    );

    const payload: TimerProfilePayload = {
      id: formModalMode === "edit" && selectedProfile ? selectedProfile.id : 0,
      assetCode,
      profileName: values.name.trim(),
      startTime: values.startTime,
      endTime: values.endTime,
      zoneID: values.zoneId ?? 0,
      daysOfWeeks: selectedDay?.id ?? 0,
      actionId: selectedAction?.id ?? 0,
      repeatAction: 0,
      brightLevel: values.brightLevel,
      dimLevel: values.dimLevel,
    };

    try {
      if (formModalMode === "edit" && selectedProfile) {
        await updateTimerProfile(payload);
      } else {
        await setTimerProfile(payload);
      }

      setFormModalMode(null);
      if (tableDataRef) {
        await tableDataRef.refetch();
        const refreshed = tableDataRef.getProfiles().find(
          (p) => p.id === selectedProfile?.id,
        );
        if (refreshed) {
          setSelectedProfile(refreshed);
          try {
            const detailResponse = await getTimerProfileById(refreshed.id);
            setProfileDetailData(detailResponse?.data || refreshed);
          } catch {
            setProfileDetailData(refreshed);
          }
        } else {
          setProfileDetailData(null);
        }
      } else {
        setProfileDetailData(null);
      }
    } catch (error: any) {
      console.error("Failed to save timer profile: ", error);
      displayToast(
        error?.response?.data?.message || "Failed to save profile.",
        "red"
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEnableDisable = (profile: TimerProfile, action: "enable" | "disable") => {
    setSelectedProfile(profile);
    setEnableDisableAction(action);
    setIsEnableDisableModalOpen(true);
  };

  const confirmEnableDisable = async () => {
    if (!selectedProfile || !enableDisableAction || !assetCode) return;

    try {
      setEnableDisableStatus("pending");
      await updateTimerProfileStatus(
        [selectedProfile.id],
        enableDisableAction === "enable",
      );
      enableDisablePendingRef.current = true;
    } catch (error: any) {
      console.error("Failed to update timer profile status: ", error);
      setEnableDisableStatus("failed");
      displayToast(
        error?.response?.data?.message || "Failed to update profile status.",
        "red",
      );
    } finally {
      setIsEnableDisableModalOpen(false);
      setEnableDisableAction(null);
    }
  };

  return (
    <div className="configuration-section">
      <TimerProfileToolbar
        onViewHistory={handleViewHistory}
        onRestore={handleRestore}
      />

      <TimerProfileTable
        assetCode={assetCode}
        selectedProfileId={selectedProfile?.profileId ?? null}
        commandStatus={commandStatus}
        commandCountdown={commandCountdown}
        isLoadingCommandResult={isLoadingCommandResult}
        onSelectProfile={handleSelectProfile}
        onGetProfileConfig={handleGetProfileConfig}
        onViewCommandResult={handleViewCommandResult}
        onAddProfile={handleAddProfile}
        onTableDataReady={(data) => setTableDataRef(data)}
      />

      {selectedProfile && (
        <TimerProfileDetailCard
          profile={profileDetailData || selectedProfile}
          zoneInfoStatus={zoneInfoStatus}
          onEdit={handleEditProfile}
          onDelete={handleDeleteProfile}
          onGetZoneInfo={handleGetZoneInfo}
          onViewZoneInfo={() => setIsZoneInfoModalOpen(true)}
          onToggleEnabled={handleEnableDisable}
          enableDisableStatus={enableDisableStatus}
          isLoading={isLoadingProfileDetail}
        />
      )}

      <TimerProfileFormModal
        isOpen={formModalMode !== null}
        mode={formModalMode ?? "add"}
        values={formValues}
        onChange={setFormValues}
        errors={timerValidation.displayedErrors}
        daysOptions={formDaysOptions}
        actionModeOptions={formActionModeOptions}
        zoneOptions={formZoneOptions}
        isSubmitting={isSubmitting}
        onClose={() => {
          setFormModalMode(null);
          setFormErrors({});
          timerValidation.reset();
        }}
        onSubmit={() => handleSaveProfile(formValues)}
        onTouchField={timerValidation.touchField}
      />

      {isEnableDisableModalOpen && selectedProfile && (
        <ModalComponent
          showModal={isEnableDisableModalOpen}
          handleClose={() => {
            setIsEnableDisableModalOpen(false);
            setEnableDisableAction(null);
          }}
          headerTitle="Command Issue"
          actionConfig={[
            {
              label: "Save",
              event: confirmEnableDisable,
              variant: "primary",
            },
          ]}
          size="md"
        >
          <ProfileEnableDisableModal
            profileName={selectedProfile.profileName}
            action={enableDisableAction || "enable"}
          />
        </ModalComponent>
      )}

      {isDeleteModalOpen && selectedProfile && (
        <ModalComponent
          showModal={isDeleteModalOpen}
          handleClose={() => setIsDeleteModalOpen(false)}
          headerTitle="Delete Profile"
          actionConfig={[
            {
              label: "Delete",
              event: confirmDelete,
              variant: "danger",
            },
          ]}
          size="md"
        >
          <p>
            Are you sure you want to delete{" "}
            <strong>{selectedProfile.profileName}</strong>? This action cannot
            be undone.
          </p>
        </ModalComponent>
      )}

      {isHistoryModalOpen && (
        <TimerProfileHistory
          assetCode={assetCode}
          isOpen={isHistoryModalOpen}
          handleClose={handleCloseHistory}
          refetchTrigger={historyRefetchTrigger}
        />
      )}

      {isCommandResultModalOpen && (
        <TimerProfileCommandResultsModal
          showModal={isCommandResultModalOpen}
          handleClose={handleCloseCommandResult}
          isLoading={isLoadingCommandResult || isLoadingBackupRestore}
          lastSyncDisplay={lastSyncDisplay}
          profiles={isRestoreMode ? baselineRows : commandResultData?.profiles ?? []}
          onBackup={handleBackup}
          onRestore={handleRestoreConfig}
          isRestoreMode={isRestoreMode}
          errorMessage={isRestoreMode ? restoreError : null}
        />
      )}

      {isZoneInfoModalOpen && (
        <AddZoneModal
          showModal={isZoneInfoModalOpen}
          handleClose={handleCloseZoneInfoModal}
          preselectedZoneId={selectedProfile?.zoneId ?? null}
          zoneOptions={zoneInfoAssets.length > 0 ? zoneInfoAssets : formZoneOptions}
          handleSave={handleSaveZone}
          isSubmitting={zoneInfoStatus === "pending"}
          fetchStatus={zoneInfoStatus}
          fetchError={zoneInfoError}
        />
      )}
    </div>
  );
};

export default TimerProfileComponent;
